import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAllTargets } from '../../helper/InitTargetHelper';
import Styles from './DevLauncher.module.scss';

// 매직 URL 모음 — 우측 상단 FAB 클릭 시 modal 로 노출.
// EntryRouter 는 기본 InitView (회사 선택 form) 위에 본 컴포넌트를 mount.
//
// 두 가지 진입 경로:
// 1. /init?target=<key>  — InitTargetHelper 의 TARGET_CONFIG_MAP 에 등록된 회사 자동 진입
// 2. /init2?...          — chatBotType / sid / chatYn / inflowType / name 을 쿼리로 받는 deep-link

const buildAbsoluteUrl = (path) =>
  `${window.location.origin}/#${path.startsWith('/') ? '' : '/'}${path}`;

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallthrough */
  }
  // 보조 fallback — execCommand
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

// 채널 모드 — chatYn + inflowType 의 짝. 회사 카드 / init2 빌더 양쪽에서 공유.
// 참고: legacy InitView 의 채팅/음성 select 는 setState 누락 버그로 백엔드 페이로드를
// 변경 못 함 (UI 와 무관하게 항상 chatYn='N' / inflowType='ARS' 전송 — playwright 로 확인).
// 본 콘솔의 진입 URL 은 mode 파라미터로 백엔드 페이로드를 명시적으로 분기.
const INIT2_MODE_OPTIONS = [
  {
    value: 'voice',
    label: '음성 (chatYn=N · inflowType=ARS)',
    chatYn: 'N',
    inflowType: 'ARS',
  },
  {
    value: 'chat',
    label: '채팅 (chatYn=Y · inflowType=CHAT)',
    chatYn: 'Y',
    inflowType: 'CHAT',
  },
];

// 회사 카드 — /init?target=<key>&mode=<mode> 바로가기 + URL 복사 + init2 빌더 prefill
const TargetCard = ({ target, onPrefillInit2 }) => {
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState('voice');
  const url = buildAbsoluteUrl(
    `/init?target=${target.key}${mode === 'chat' ? '&mode=chat' : ''}`,
  );
  const name = target.companyName ?? target.label ?? target.key;

  const handleCopy = async () => {
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    }
  };

  const handleGo = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const descParts = [`target=${target.key}`];
  if (target.companyTel) descParts.push(`tel ${target.companyTel}`);
  if (target.sid) descParts.push(`sid ${target.sid}`);

  return (
    <div className={Styles.Card}>
      <div className={Styles.CardHead}>
        <span className={Styles.CardName}>{name}</span>
        <span className={Styles.Tag}>id {target.id}</span>
      </div>
      <div className={Styles.CardDesc}>{descParts.join(' · ')}</div>
      <div className={Styles.BuilderRow}>
        <span className={Styles.BuilderLabel}>모드</span>
        <select
          className={Styles.BuilderInput}
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          {INIT2_MODE_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>
      <div className={Styles.Url}>{url}</div>
      <div className={Styles.Actions}>
        <button
          type="button"
          className={`${Styles.Btn} ${Styles.BtnPrimary}`}
          onClick={handleGo}
        >
          바로가기
        </button>
        <button
          type="button"
          className={`${Styles.Btn} ${Styles.BtnSecondary} ${copied ? Styles.CopiedFlash : ''}`}
          onClick={handleCopy}
        >
          {copied ? '복사 완료' : 'URL 복사'}
        </button>
        {(target.companyTel || target.sid) && (
          <button
            type="button"
            className={`${Styles.Btn} ${Styles.BtnSecondary}`}
            onClick={() => onPrefillInit2(target)}
            title="아래 /init2 빌더에 회사 전화번호와 sid 를 자동 입력"
          >
            init2 빌더 채우기
          </button>
        )}
      </div>
    </div>
  );
};

const INIT2_FORM_DEFAULT = {
  chatBotType: '',
  sid: '',
  chatYn: 'N',
  inflowType: 'ARS',
  name: '',
};

// /init2 파라미터 빌더 — controlled. 부모가 form state 관리 → 회사 카드 prefill 연결.
const Init2Builder = ({ form, onChange, builderRef }) => {
  const [copied, setCopied] = useState(false);

  const url = useMemo(() => {
    const params = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) params[k] = v;
    });
    const qs = new URLSearchParams(params).toString();
    return buildAbsoluteUrl(`/init2${qs ? `?${qs}` : ''}`);
  }, [form]);

  const ready = !!(form.chatBotType && form.sid);

  const setField = (key) => (e) => {
    onChange({ ...form, [key]: e.target.value });
  };

  const currentMode =
    INIT2_MODE_OPTIONS.find(
      (m) => m.chatYn === form.chatYn && m.inflowType === form.inflowType,
    )?.value ?? INIT2_MODE_OPTIONS[0].value;

  const handleModeChange = (e) => {
    const m = INIT2_MODE_OPTIONS.find((o) => o.value === e.target.value);
    if (!m) return;
    onChange({ ...form, chatYn: m.chatYn, inflowType: m.inflowType });
  };

  const handleCopy = async () => {
    if (!ready) return;
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    }
  };

  const handleGo = () => {
    if (!ready) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleReset = () => onChange({ ...INIT2_FORM_DEFAULT });

  return (
    <div
      ref={builderRef}
      className={Styles.Card}
      style={{ gridColumn: '1 / -1' }}
    >
      <div className={Styles.CardHead}>
        <span className={Styles.CardName}>/init2 파라미터 빌더</span>
        <span className={Styles.Tag}>deep-link</span>
      </div>
      <div className={Styles.CardDesc}>
        chatBotType (전화번호) 와 sid (시나리오 ID) 는 필수. 위 회사 카드의 「init2 빌더 채우기」 로 자동 입력 가능 (sid 가 없는 회사는 직접 입력).
      </div>

      <div className={Styles.BuilderRow}>
        <span className={Styles.BuilderLabel}>chatBotType *</span>
        <input
          className={Styles.BuilderInput}
          placeholder="예: 02-2172-6700"
          value={form.chatBotType}
          onChange={setField('chatBotType')}
        />
      </div>
      <div className={Styles.BuilderRow}>
        <span className={Styles.BuilderLabel}>sid *</span>
        <input
          className={Styles.BuilderInput}
          placeholder="예: SID1001"
          value={form.sid}
          onChange={setField('sid')}
        />
      </div>
      <div className={Styles.BuilderRow}>
        <span className={Styles.BuilderLabel}>채널 모드</span>
        <select
          className={Styles.BuilderInput}
          value={currentMode}
          onChange={handleModeChange}
        >
          {INIT2_MODE_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>
      <div className={Styles.BuilderRow}>
        <span className={Styles.BuilderLabel}>name</span>
        <input
          className={Styles.BuilderInput}
          placeholder="비우면 랜덤 닉네임 자동 생성"
          value={form.name}
          onChange={setField('name')}
        />
      </div>

      <div className={Styles.Url}>{url}</div>
      <div className={Styles.Actions}>
        <button
          type="button"
          className={`${Styles.Btn} ${Styles.BtnPrimary}`}
          onClick={handleGo}
          disabled={!ready}
          style={!ready ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          바로가기
        </button>
        <button
          type="button"
          className={`${Styles.Btn} ${Styles.BtnSecondary} ${copied ? Styles.CopiedFlash : ''}`}
          onClick={handleCopy}
          disabled={!ready}
          style={!ready ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          {copied ? '복사 완료' : 'URL 복사'}
        </button>
        <button
          type="button"
          className={`${Styles.Btn} ${Styles.BtnSecondary}`}
          onClick={handleReset}
        >
          초기화
        </button>
      </div>
    </div>
  );
};

// Modal dialog 형태로 매직 URL 카드들을 노출. onClose 호출 시 닫힘.
const DevLauncher = ({ onClose }) => {
  const targets = getAllTargets();
  const overlayRef = useRef(null);
  const builderRef = useRef(null);

  const [init2Form, setInit2Form] = useState({ ...INIT2_FORM_DEFAULT });

  // Escape 키로 닫기
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleOverlayClick = (e) => {
    // 배경 (overlay 자체) 클릭만 닫기, dialog 내부 클릭은 통과
    if (e.target === overlayRef.current) onClose?.();
  };

  // 회사 카드 → /init2 빌더 prefill. 기존 값은 회사 데이터가 비어있을 때만 보존.
  const handlePrefillInit2 = (target) => {
    setInit2Form((prev) => ({
      ...prev,
      chatBotType: target.companyTel || prev.chatBotType,
      sid: target.sid || prev.sid,
    }));
    builderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div
      ref={overlayRef}
      className={Styles.Overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dev-launcher-title"
    >
      <div className={Styles.Dialog} style={{ position: 'relative' }}>
        <button
          type="button"
          className={Styles.CloseButton}
          onClick={() => onClose?.()}
          aria-label="닫기"
        >
          ×
        </button>
        <div className={Styles.DialogBody}>
          <div className={Styles.Container}>
            <div className={Styles.Header}>
              <div id="dev-launcher-title" className={Styles.Title}>
                개발자 진입 콘솔
              </div>
              <div className={Styles.Subtitle}>
                매직 URL 모음. 「바로가기」는 새 탭으로 진입, 「URL 복사」는 클립보드 복사.
              </div>
            </div>

            <div className={Styles.Section}>
              <div className={Styles.SectionTitle}>
                회사별 바로 진입 (/init?target=) — TARGET_CONFIG_MAP 등록 회사만
              </div>
              <div className={Styles.Grid}>
                {targets.map((t) => (
                  <TargetCard
                    key={t.key}
                    target={t}
                    onPrefillInit2={handlePrefillInit2}
                  />
                ))}
              </div>
            </div>

            <div className={Styles.Section}>
              <div className={Styles.SectionTitle}>
                deep-link 채널 진입 (/init2) — 임의 chatBotType + sid 조합
              </div>
              <div className={Styles.Grid}>
                <Init2Builder
                  form={init2Form}
                  onChange={setInit2Form}
                  builderRef={builderRef}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevLauncher;
